<template>
  <div class="modal" @click.self="closeModal">
    <div class="modal-content">
      <div class="form-group">
        <ToggleButton v-model="activateDetailForPreparationTime" label="Régler par repas" />
      </div>

      <div v-if="!activateDetailForPreparationTime" class="form-group">
        <label class="form-label">Valeur (appliquée à tous les repas)</label>
        <select
          :key="selectRenderKey('meal_mode_monday_lunch')"
          class="form-select"
          v-model="form.meal_mode_monday_lunch"
        >
          <option value="max_30">30 minutes</option>
          <option value="max_40">40 minutes</option>
          <option value="max_50">50 minutes</option>
          <option value="over_60">+1 heure</option>
        </select>
      </div>

      <div v-else>
        <div class="form-group" v-for="(row, idx) in mealRows" :key="idx">
          <div class="row">
            <div class="col-md-6">
              <label class="form-label">{{ row.left.label }}</label>
              <select
                :key="selectRenderKey(row.left.key)"
                class="form-select"
                v-model="form[row.left.key]"
              >
                <option value="outside">Je prends ce repas à l’extérieur</option>
                <option value="leftovers" :disabled="isLeftoversOptionDisabled(row.left.key)">
                  J’utilise les restes du repas précédent
                </option>
                <option value="max_30">J’ai maximum 30 minutes pour cuisiner</option>
                <option value="max_40">J’ai maximum 40 minutes pour cuisiner</option>
                <option value="max_50">J’ai maximum 50 minutes pour cuisiner</option>
                <option value="over_60">J’ai plus d’une heure pour cuisiner</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">{{ row.right.label }}</label>
              <select
                :key="selectRenderKey(row.right.key)"
                class="form-select"
                v-model="form[row.right.key]"
              >
                <option value="outside">Je prends ce repas à l’extérieur</option>
                <option value="leftovers" :disabled="isLeftoversOptionDisabled(row.right.key)">
                  J’utilise les restes du repas précédent
                </option>
                <option value="max_30">J’ai maximum 30 minutes pour cuisiner</option>
                <option value="max_40">J’ai maximum 40 minutes pour cuisiner</option>
                <option value="max_50">J’ai maximum 50 minutes pour cuisiner</option>
                <option value="over_60">J’ai plus d’une heure pour cuisiner</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; margin-top: 1rem; gap: 10px;">
        <button class="button" @click="closeModal">
          Fermer
        </button>

        <button class="button" @click="save" :disabled="saving">
          Sauvegarder
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ToggleButton from "@/components/ToggleButton.vue";
import { userState } from "@/utils/store";

export default {
  name: "EditPreparationTimeModal",
  components: {
    ToggleButton,
  },
  props: {
    preferences: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activateDetailForPreparationTime: false,
      formRenderVersion: 0,
      form: {
        meal_mode_monday_lunch: this.preferences.meal_mode_monday_lunch || "max_30",
        meal_mode_monday_dinner: this.preferences.meal_mode_monday_dinner || "max_30",
        meal_mode_tuesday_lunch: this.preferences.meal_mode_tuesday_lunch || "max_30",
        meal_mode_tuesday_dinner: this.preferences.meal_mode_tuesday_dinner || "max_30",
        meal_mode_wednesday_lunch: this.preferences.meal_mode_wednesday_lunch || "max_30",
        meal_mode_wednesday_dinner: this.preferences.meal_mode_wednesday_dinner || "max_30",
        meal_mode_thursday_lunch: this.preferences.meal_mode_thursday_lunch || "max_30",
        meal_mode_thursday_dinner: this.preferences.meal_mode_thursday_dinner || "max_30",
        meal_mode_friday_lunch: this.preferences.meal_mode_friday_lunch || "max_30",
        meal_mode_friday_dinner: this.preferences.meal_mode_friday_dinner || "max_30",
        meal_mode_saturday_lunch: this.preferences.meal_mode_saturday_lunch || "max_30",
        meal_mode_saturday_dinner: this.preferences.meal_mode_saturday_dinner || "max_30",
        meal_mode_sunday_lunch: this.preferences.meal_mode_sunday_lunch || "max_30",
        meal_mode_sunday_dinner: this.preferences.meal_mode_sunday_dinner || "max_30",
      },
      saving: false,
    };
  },
  computed: {
    mealKeys() {
      return [
        "meal_mode_monday_lunch",
        "meal_mode_monday_dinner",
        "meal_mode_tuesday_lunch",
        "meal_mode_tuesday_dinner",
        "meal_mode_wednesday_lunch",
        "meal_mode_wednesday_dinner",
        "meal_mode_thursday_lunch",
        "meal_mode_thursday_dinner",
        "meal_mode_friday_lunch",
        "meal_mode_friday_dinner",
        "meal_mode_saturday_lunch",
        "meal_mode_saturday_dinner",
        "meal_mode_sunday_lunch",
        "meal_mode_sunday_dinner",
      ];
    },
    leftoversDisabledMap() {
      const disabledMap = {};

      this.mealKeys.forEach((mealKey, index) => {
        if (!this.activateDetailForPreparationTime) {
          // En mode global, les restes ne sont jamais sélectionnables
          disabledMap[mealKey] = true;
          return;
        }

        if (index === 0) {
          // Lundi midi : pas d'historique, restes impossible
          disabledMap[mealKey] = true;
          return;
        }

        const prevKey = this.mealKeys[index - 1];
        const prevValue = this.form[prevKey];
        // Désactivé si le repas précédent n'est pas cuisiné
        disabledMap[mealKey] = prevValue === "outside" || prevValue === "leftovers";
      });

      return disabledMap;
    },
    mealRows() {
      return [
        { left: { label: "Lundi midi", key: "meal_mode_monday_lunch" }, right: { label: "Lundi soir", key: "meal_mode_monday_dinner" } },
        { left: { label: "Mardi midi", key: "meal_mode_tuesday_lunch" }, right: { label: "Mardi soir", key: "meal_mode_tuesday_dinner" } },
        { left: { label: "Mercredi midi", key: "meal_mode_wednesday_lunch" }, right: { label: "Mercredi soir", key: "meal_mode_wednesday_dinner" } },
        { left: { label: "Jeudi midi", key: "meal_mode_thursday_lunch" }, right: { label: "Jeudi soir", key: "meal_mode_thursday_dinner" } },
        { left: { label: "Vendredi midi", key: "meal_mode_friday_lunch" }, right: { label: "Vendredi soir", key: "meal_mode_friday_dinner" } },
        { left: { label: "Samedi midi", key: "meal_mode_saturday_lunch" }, right: { label: "Samedi soir", key: "meal_mode_saturday_dinner" } },
        { left: { label: "Dimanche midi", key: "meal_mode_sunday_lunch" }, right: { label: "Dimanche soir", key: "meal_mode_sunday_dinner" } },
      ];
    },
  },
  mounted() {
    const v = this.form.meal_mode_monday_lunch;
    const all = this.mealKeys.map((key) => this.form[key]);

    this.activateDetailForPreparationTime = all.some((x) => x !== v);
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.formRenderVersion += 1;
        // Auto-reset : si un repas passe à "leftovers" alors que c'est maintenant invalide, on le remet à max_30
        this.mealKeys.forEach((mealKey) => {
          if (this.form[mealKey] === "leftovers" && this.leftoversDisabledMap[mealKey]) {
            this.form[mealKey] = "max_30";
          }
        });
      },
    },
    activateDetailForPreparationTime(isDetailedMode) {
      if (!isDetailedMode && this.form.meal_mode_monday_lunch === "leftovers") {
        this.form.meal_mode_monday_lunch = "max_30";
      }
    },
  },
  methods: {
    isLeftoversOptionDisabled(mealKey) {
      if (this.form[mealKey] === "leftovers") {
        return false;
      }

      return !!this.leftoversDisabledMap[mealKey];
    },
    selectRenderKey(mealKey) {
      return [
        mealKey,
        this.activateDetailForPreparationTime ? "detailed" : "global",
        this.formRenderVersion,
      ].join("-");
    },
    closeModal() {
      this.$emit("close");
    },
    save() {
      this.saving = true;
      userState.globalSaving = true;

      let body;

      if (this.activateDetailForPreparationTime) {
        body = { ...this.form };
      } else {
        const v = this.form.meal_mode_monday_lunch;
        body = {
          meal_mode_monday_lunch: v,
          meal_mode_monday_dinner: v,
          meal_mode_tuesday_lunch: v,
          meal_mode_tuesday_dinner: v,
          meal_mode_wednesday_lunch: v,
          meal_mode_wednesday_dinner: v,
          meal_mode_thursday_lunch: v,
          meal_mode_thursday_dinner: v,
          meal_mode_friday_lunch: v,
          meal_mode_friday_dinner: v,
          meal_mode_saturday_lunch: v,
          meal_mode_saturday_dinner: v,
          meal_mode_sunday_lunch: v,
          meal_mode_sunday_dinner: v,
        };
      }

      userState
        .savePreferences(body)
        .then((response) => {
          this.$emit("update", response?.consumer_account || null);
          if (response?.warning) {
            alert(response.warning);
          }
          window.location.reload();
        })
        .catch(() => {
          userState.globalSaving = false;
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}
</style>
